import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProfileService } from '../../services/profile.service';
import {
  CampaignFilterModalComponent,
  CampaignFilterOptions,
} from '../campaign-filter-modal/campaign-filter-modal.component';
import { BaseToolbarComponent } from '../shared/base-toolbar.component';

export interface ViewType {
  value: 'list' | 'calendar';
  label: string;
  icon: string;
}

/**
 * Toolbar component for the campaign area that provides search, view switching, and filter modal controls for campaign lists and calendar views.
 * It displays the current active filter count, opens and closes a filter modal, and emits events when the view or filters change.
 * Extends BaseToolbarComponent to inherit common toolbar behavior and initializes the search placeholder via the injected ProfileService.
 * Key methods:
 *   - onFilterClick(): Opens the filter modal and emits filterClicked.
 *   - onFilterModalClose(): Closes the filter modal.
 *   - onFiltersApplied(filters): Emits filtersApplied with the selected filters and closes the modal.
 *   - onFiltersCleared(): Emits filtersCleared; the modal stays open (CWS-509).
 *   - getActiveFilterCount(): Computes the number of active filter selections (statuses, campaignTypes, locations, sortBy).
 * Lifecycle hooks:
 *   - ngOnInit(): Component initialization logic (inherited or implemented as needed).
 *   - ngOnDestroy(): Cleanup when the component is destroyed.
 * @component
 * @selector app-campaign-toolbar
 * @example
 *   <app-campaign-toolbar
 *     [activeFilters]="sampleFilters"
 *     (viewChanged)="onViewChanged($event)"
 *     (filterClicked)="onFilterClicked()"
 *     (filtersApplied)="onFiltersApplied($event)"
 *     (filtersCleared)="onFiltersCleared()"
 *   ></app-campaign-toolbar>
 * @input {CampaignFilterOptions | null} activeFilters - Optional current filter state used to indicate and compute active filters; populates and reflects filter selections in the toolbar.
 * @output {EventEmitter<'list' | 'calendar'>} viewChanged - Emitted when the user changes the display between 'list' and 'calendar' views.
 * @output {EventEmitter<void>} filterClicked - Emitted when the filter button is clicked and the filter modal is requested to open.
 * @output {EventEmitter<CampaignFilterOptions>} filtersApplied - Emitted when the user applies filters from the filter modal; payload contains the selected CampaignFilterOptions.
 * @output {EventEmitter<void>} filtersCleared - Emitted when the user clears all filters via the toolbar or modal.
 */
@Component({
  selector: 'app-campaign-toolbar',
  standalone: true,
  imports: [CommonModule, FormsModule, CampaignFilterModalComponent],
  templateUrl: './campaign-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-toolbar.component.scss',
})
export class CampaignToolbarComponent extends BaseToolbarComponent implements OnInit, OnDestroy {
  @Input() activeFilters: CampaignFilterOptions | null = null;

  @Output() viewChanged = new EventEmitter<'list' | 'calendar'>();
  @Output() filterClicked = new EventEmitter<void>();
  @Output() filtersApplied = new EventEmitter<CampaignFilterOptions>();
  @Output() filtersCleared = new EventEmitter<void>();

  isFilterModalOpen = false;

  constructor(profileService: ProfileService) {
    super(profileService);
    this.searchPlaceholder = 'Search for campaigns';
  }

  onFilterClick(): void {
    this.isFilterModalOpen = true;
    this.filterClicked.emit();
  }

  onFilterModalClose(): void {
    this.isFilterModalOpen = false;
  }

  onFiltersApplied(filters: CampaignFilterOptions): void {
    this.filtersApplied.emit(filters);
    this.isFilterModalOpen = false;
  }

  onFiltersCleared(): void {
    this.filtersCleared.emit();
    // CWS-509: leave the popup open so the user can re-select without reopening it.
  }

  getActiveFilterCount(): number {
    if (!this.activeFilters) return 0;
    let count = 0;
    count += this.activeFilters.statuses?.length || 0;
    count += this.activeFilters.campaignTypes?.length || 0;
    count += this.activeFilters.locations?.length || 0;
    if (this.activeFilters.sortBy) count += 1;
    return count;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
